import { cloneDeep, merge } from 'lodash'

import { DateFields, getBackendGeneration, getServicePathToAPI, joinURLPath } from '@sigma-frontend-libs/commons'
import { Macro } from '@sigma-legacy-libs/cache'
import { OrderDirections } from '@sigmasms/shared-types'

import { macroCacheOptions, projectName, request } from '@/utils'

/**
 * Кеш элементов по строковому представлению значения. Позволяет
 * показать заголовок выбранного элемента, которого нет в текущем
 * списке. Общий для всех экземпляров компонента.
 *
 * @type {Map<string, object>}
 */
export const cachedItems = new Map()

/**
 * Кеш ответов сервиса с настройками macroCacheOptions.
 */
const Cache = new Macro(macroCacheOptions)

/**
 * Запрашивает список элементов сервиса и кеширует ответ по ключу.
 *
 * @param {string} key Ключ кеша.
 * @param {string} service Имя сервиса.
 * @param {object} [params] Параметры запроса.
 * @param {Record<string, any>} [params.query] Строка запроса.
 * @returns {Promise<{ data?: Array<object> }>} Ответ сервиса.
 */
const cachedFind = Cache.wrapWithCache(async (key, service, params) => {
  const pathToAPI = getServicePathToAPI(service)

  return await request.find(pathToAPI, undefined, params?.query)
})

/**
 * Запрашивает один элемент сервиса по идентификатору и кеширует ответ
 * по ключу.
 *
 * @param {string} key Ключ кеша.
 * @param {string} service Имя сервиса.
 * @param {string} id Идентификатор элемента.
 * @returns {Promise<object|undefined>} Элемент сервиса.
 */
const cachedGet = Cache.wrapWithCache(async (key, service, id) => {
  const pathToAPI = getServicePathToAPI(service)

  return await request.get(joinURLPath(pathToAPI, id))
})

/**
 * Убирает префикс `$` у ключей строки запроса.
 * Если ключ начинается с `$`, в результат попадает ключ без этого
 * символа, иначе ключ переносится без изменений. Значения не меняются.
 *
 * @param {Record<string, any>} query Строка запроса.
 * @returns {Record<string, any>} Строка запроса с очищенными ключами.
 */
const removeDollarSigns = query => {
  const result = {}
  for (const key in query) {
    let newKey = key
    if (key.startsWith('$')) {
      newKey = key.slice(1)
    }
    result[newKey] = query[key]
  }

  return result
}

/**
 * Значение по умолчанию для свойства requestItems.
 * Вызывается в контексте компонента.
 *
 * Запрашивается первая страница из 25 элементов, отсортированных по
 * дате создания по убыванию, с учетом строки поиска.
 *
 * Если backendGeneration не задан, поколение определяется по имени
 * сервиса. Если поколение равно 'nest', префиксы `$` у ключей строки
 * запроса убираются. Если задано свойство query, его поля добавляются
 * к строке запроса.
 *
 * @returns {Promise<Array<object>>} Список элементов из сервиса или пустой
 * массив, если ответ не содержит непустого массива data.
 */
export async function requestItems() {
  const params = {
    query: {
      $offset: 0,
      $limit: 25,
      $order: [ [ DateFields.createdAt, OrderDirections.DESC ] ],
      $search: this.search || undefined
    }
  }
  const generation = this.backendGeneration ?? getBackendGeneration(this.service)
  if (generation === 'nest') {
    params.query = removeDollarSigns(params.query)
  }
  if (this.query) {
    merge(params.query, cloneDeep(this.query))
  }
  const key = [ projectName, this.service, 'find', JSON.stringify(params) ].filter(Boolean).join(':')
  const result = await cachedFind(key, this.service, params) || {}
  if (result && Array.isArray(result.data) && result.data.length) {
    return result.data
  }

  return []
}

/**
 * Значение по умолчанию для свойства requestItem.
 * Вызывается в контексте компонента.
 *
 * @param {string} id Идентификатор элемента.
 * @returns {Promise<object|undefined>} Элемент из сервиса или undefined,
 * если элемент не найден.
 */
export async function requestItem(id) {
  const key = [ projectName, this.service, 'get', id ].filter(Boolean).join(':')

  return await cachedGet(key, this.service, id)
}
